<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import GlobalControls from '@/components/GlobalControls.vue';
import BottomNav from '@/components/BottomNav.vue';
import DailyChallengePopup from '@/components/DailyChallengePopup.vue';
import { useAuthStore } from '@/stores/authStore';
import { useOnlineStore } from '@/stores/onlineStore';
import { useWakeLock } from '@/composables/useWakeLock';
import { isAppShell } from '@/composables/useNativeApp';
import { isPushSupported, isSubscribed, subscribeToPush, refreshSubscriptionState } from '@/composables/usePushNotifications';

const route = useRoute();
const authStore = useAuthStore();
const onlineStore = useOnlineStore();
const wakeLock = useWakeLock();

// Notif "défi du jour" activée par défaut : on tente l'abonnement automatiquement
// à la 1re connexion plutôt que d'attendre que l'utilisateur clique dans Profil.
// Un seul essai auto (flag localStorage) : si le navigateur/l'OS a refusé la permission,
// on ne re-sollicite plus tout seul (le bouton manuel dans Profil reste disponible).
const AUTO_PUSH_KEY = 'tchinquiz.pushAutoPrompted';
async function maybeAutoSubscribePush() {
  if (!isPushSupported || localStorage.getItem(AUTO_PUSH_KEY)) return;
  localStorage.setItem(AUTO_PUSH_KEY, '1');
  await refreshSubscriptionState();
  if (!isSubscribed.value) {
    try { await subscribeToPush(); } catch { /* permission refusée ou erreur réseau — best-effort */ }
  }
}

// Menu bas d'écran : app native OU PWA installée (écran d'accueil), masqué uniquement
// pendant le jeu actif (une fois la partie lancée) pour ne pas distraire. Visible pour
// toutes les étapes avant/après (Setup, Ordre, Lobby, Résumé).
const GAME_FLOW_ROUTES = new Set(['game']);
const showBottomNav = computed(() => isAppShell && !GAME_FLOW_ROUTES.has(String(route.name)));

// En navigateur classique (hors PWA/app native), le menu du bas n'existe pas,
// donc /admin (et /admin/grid) est inatteignable par l'UI pour un admin sur PC.
// Lien discret fixe, uniquement dans ce contexte.
const showDesktopAdminLink = computed(() => !isAppShell && authStore.isAdmin);

// registerType 'prompt' + pas de skipWaiting() côté SW (cf. sw.ts) : la nouvelle version
// reste en attente tant que l'utilisateur n'a pas cliqué sur "Recharger". Ça évite un
// reload en pleine session (ex. juste après un login, avant la redirection) qui a déjà
// fait retomber des utilisateurs sur l'écran de login (cf. bug régression 2026-08-27).
const updateAvailable = ref(false);
const { updateServiceWorker } = useRegisterSW({
  immediate: true,
  onNeedRefresh() { updateAvailable.value = true; },
  onOfflineReady() {},
});
function applyUpdate() {
  updateServiceWorker(true);
}

onMounted(async () => {
  wakeLock.request();
  await authStore.init();
  if (authStore.isLoggedIn) {
    try { await onlineStore.tryResume(); } catch {}
    maybeAutoSubscribePush();
  }
});

// Si le user se logue plus tard depuis la page de login, on retente
watch(() => authStore.isLoggedIn, async (logged) => {
  if (logged) {
    try { await onlineStore.tryResume(); } catch {}
    maybeAutoSubscribePush();
  }
});
</script>

<template>
  <div class="app-shell" :class="{ 'with-bottom-nav': showBottomNav }">
    <GlobalControls />
    <RouterView />
    <BottomNav v-if="showBottomNav" />
    <DailyChallengePopup v-if="authStore.isLoggedIn" />
    <a v-if="showDesktopAdminLink" href="#/admin/grid" class="desktop-admin-link">🗂 Banque de questions</a>
    <div v-if="updateAvailable" class="update-banner">
      <span>Nouvelle version disponible</span>
      <button class="update-btn" @click="applyUpdate">Recharger</button>
    </div>
  </div>
</template>

<style scoped>
.app-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.app-shell.with-bottom-nav {
  padding-bottom: calc(58px + env(safe-area-inset-bottom));
}
.desktop-admin-link {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 90;
  background: var(--card);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(0,0,0,0.25);
}
.desktop-admin-link:hover {
  border-color: var(--primary);
}
.update-banner {
  position: fixed;
  left: 50%;
  bottom: calc(16px + env(safe-area-inset-bottom));
  transform: translateX(-50%);
  z-index: 95;
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--card);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 8px 8px 8px 16px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(0,0,0,0.25);
  white-space: nowrap;
}
.update-btn {
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 999px;
  padding: 7px 14px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}
</style>
